export const ROLE_TRANSITIONS = {
  CREATOR: [["To Do", "Prepare"], ["Prepare", "Create"], ["Create", "Review"]],
  REVIEWER: [["Review", "Create"], ["Review", "Approval"]],
  FINAL_APPROVER: [["Approval", "Review"], ["Approval", "Complete"]],
};

export const ACCESS_ADMIN_EMAIL = "anand@livelifeeducation.com";

export function isAdmin(user) {
  return !!user && (user.role === "manager" || (user.roles || []).includes("ADMIN"));
}

// ADMIN access is ROLE-BASED: any user with the ADMIN role can manage users and
// reach every admin area.
export function canAccessControl(user) {
  return isAdmin(user);
}

// Dashboard is visible to ADMIN, REPORTING PERSON, REVIEWER and FINAL_APPROVER.
// A pure CREATOR (no team, only the CREATOR role) must not see it.
export function canViewDashboard(user) {
  if (!user) return false;
  const roles = user.roles || [];
  return isAdmin(user) || !!user.has_team || roles.includes("REVIEWER") || roles.includes("FINAL_APPROVER");
}

// Only ADMIN, REVIEWER and FINAL_APPROVER may create new Work Items. CREATOR cannot.
export function canCreateAsset(user) {
  if (!user) return false;
  const roles = user.roles || [];
  return isAdmin(user) || roles.includes("REVIEWER") || roles.includes("FINAL_APPROVER");
}

// Admin may edit master fields anytime; the owner may save the creative/source
// and attach files while the item is in the Create stage.
export function canEditCreative(user, asset) {
  if (!user || !asset) return false;
  if (isAdmin(user)) return true;
  const lead = asset.work_lead_id || asset.owner_id;
  return ["To Do", "Prepare", "Create"].includes(asset.current_stage) && lead === user.user_id;
}

// Admin may set the Google Drive source link anytime; the Creative Owner sets it
// during the production stages (required before Complete). Reviewers and Final
// Approvers can open the link but must NOT be able to change it.
export function canSetSource(user, asset) {
  if (!user) return false;
  if (isAdmin(user)) return true;
  return canEditCreative(user, asset);
}

export function canApprove(user) {
  return isAdmin(user) || (user?.roles || []).includes("FINAL_APPROVER");
}

// The only valid stage moves, for EVERYONE (admin included — the UI never offers
// anything else). Archive is not a stage; COMPLETE has no normal move.
const VALID_TRANSITIONS = [
  ["To Do", "Prepare"],
  ["Prepare", "Create"],
  ["Create", "Review"],
  ["Review", "Create"],
  ["Review", "Approval"],
  ["Approval", "Review"],
  ["Approval", "Complete"],
];

const STAGES = ["To Do", "Prepare", "Create", "Review", "Approval", "Complete"];

export function canTransition(user, from, to) {
  if (!user || from === to) return false;
  if (!STAGES.includes(from) || !STAGES.includes(to)) return false;
  // Admin has full freedom: move to and fro across every stage.
  if (isAdmin(user)) return true;
  // Employees are limited to the valid workflow moves for their role.
  if (from === "Complete") return false;
  if (!VALID_TRANSITIONS.some(([f, t]) => f === from && t === to)) return false;
  const roles = user.roles || [];
  return roles.some((r) => (ROLE_TRANSITIONS[r] || []).some(([f, t]) => f === from && t === to));
}

// stages this user may move the item INTO from its current stage
export function allowedTargets(user, from) {
  return STAGES.filter((s) => canTransition(user, from, s));
}
